/**
 * The typed failure every platform API transport raises, and how to read one
 * off the wire.
 *
 * {@link PlatformApiError} carries the mapped HTTP status, the stable wire
 * `code`, optional structured `details`, and the request correlation id. UI code
 * branches on `status` and `code`; it never parses messages.
 *
 * A leaf module on purpose: the auth provider's pre-shell session calls read
 * failures with {@link wireErrors} too, and must not pull a platform runtime
 * into the auth bundle to do it.
 *
 * @module platform/api-error
 */

/**
 * One entry of a GraphQL response's `errors` array, as this platform sends it.
 *
 * `extensions` is where the platform's own error contract lives now: the same
 * `code`, mapped `status`, and `details` the body used to carry at the top
 * level. Everything is optional because a spec-compliant error need not carry
 * extensions at all.
 */
export interface GraphqlWireError {
  message?: string;
  path?: (string | number)[];
  extensions?: {
    code?: string;
    /** Whatever the wire gave; {@link wireStatus} reads it. */
    status?: unknown;
    details?: unknown;
  };
}

/**
 * The HTTP status an `errors` entry maps its failure to, or nothing when it
 * gives none that can be one.
 */
export function wireStatus(failure: GraphqlWireError): number | undefined {
  const status = failure.extensions?.status;
  return typeof status === "number" && Number.isInteger(status) && status >= 100 && status <= 599
    ? status
    : undefined;
}

/**
 * Read the `errors` entries out of a parsed GraphQL response.
 *
 * @param payload - The parsed response body, whatever it turned out to be.
 * @returns The error entries, empty when the response carries none.
 */
export function wireErrors(payload: unknown): GraphqlWireError[] {
  if (!payload || typeof payload !== "object") {
    return [];
  }
  const errors = (payload as { errors?: unknown }).errors;
  return Array.isArray(errors) ? (errors as GraphqlWireError[]) : [];
}

export class PlatformApiError extends Error {
  readonly status: number;

  readonly code?: string;

  readonly details?: unknown;

  readonly requestId?: string;

  constructor(
    message: string,
    options: { status: number; code?: string; details?: unknown; requestId?: string },
  ) {
    super(message);
    this.name = "PlatformApiError";
    this.status = options.status;
    this.code = options.code;
    this.details = options.details;
    this.requestId = options.requestId;
  }
}
